import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  computed,
  effect,
  inject,
  Injectable,
  PLATFORM_ID,
  type Renderer2,
  RendererFactory2,
  signal,
} from '@angular/core';

import { type ColorPalette, DEFAULT_THEME, type Theme, type ThemeMode } from '../models/Theme';

import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  // =============================================================================
  // Public Properties
  // =============================================================================

  // Signals for reactive state management
  readonly currentTheme = signal<Theme>(this.getInitialTheme());
  readonly contestPalette = signal<ColorPalette | null>(null);
  readonly isThemeEnforced = computed(() => this.contestPalette() !== null);

  // =============================================================================
  // Private Properties
  // =============================================================================

  private readonly document = inject(DOCUMENT);
  private readonly storageService = inject(StorageService);
  private readonly rendererFactory: RendererFactory2 = inject(RendererFactory2);
  private readonly platformId = inject(PLATFORM_ID);
  private renderer: Renderer2;
  private darkThemeMediaQuery: MediaQueryList | null = null;

  // =============================================================================
  // Constructor
  // =============================================================================

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);

    if (isPlatformBrowser(this.platformId)) {
      this.darkThemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      // Listener for system preference changes
      this.darkThemeMediaQuery?.addEventListener('change', () => {
        if (this.currentTheme().mode === 'system') {
          this.applyTheme(this.currentTheme(), this.contestPalette());
        }
      });
    }

    // Effect to react to theme changes
    effect(() => {
      const theme = this.currentTheme();
      const contestPalette = this.contestPalette();

      // Always save the user's preference
      this.storageService.setTheme(theme);

      this.applyTheme(theme, contestPalette);
    });

    // Initial theme setup
    this.applyTheme(this.currentTheme(), this.contestPalette());
  }

  // =============================================================================
  // Public Methods
  // =============================================================================

  setTheme(theme: Theme): void {
    this.currentTheme.set(theme);
  }

  setContestTheme(contest: {
    colorSchema?: {
      primary: string;
      secondary: string;
      tertiary: string;
    } | null;
  }): void {
    if (contest.colorSchema) {
      const palette = contest.colorSchema; // colorSchema is already the object
      if (palette.primary && palette.secondary && palette.tertiary) {
        this.contestPalette.set({
          id: 'contest-theme',
          name: 'Contest Theme',
          primary: palette.primary,
          secondary: palette.secondary,
          tertiary: palette.tertiary,
        });
      }
    } else {
      this.clearContestTheme();
    }
  }

  clearContestTheme(): void {
    this.contestPalette.set(null);
  }

  // =============================================================================
  // Private Methods & Initialization Logic
  // =============================================================================

  private getInitialTheme(): Theme {
    try {
      return this.storageService.getTheme() || DEFAULT_THEME;
    } catch {
      return DEFAULT_THEME;
    }
  }

  private applyTheme(theme: Theme, contestPalette: ColorPalette | null): void {
    // Clear any existing theme attributes
    this.renderer.removeAttribute(this.document.documentElement, 'data-theme');

    // Determine the effective dark mode state
    const isDarkMode = this.getEffectiveDarkMode(theme.mode);

    // Apply theme mode using Material 3's approach
    this.renderer.setAttribute(
      this.document.documentElement,
      'data-theme',
      isDarkMode ? 'dark' : 'light',
    );

    // Clear any existing color-scheme
    this.document.documentElement.style.removeProperty('color-scheme');

    // Apply Material 3 color tokens
    this.document.documentElement.style.setProperty('color-scheme', isDarkMode ? 'dark' : 'light');

    const paletteToApply = contestPalette || theme.palette;

    // Apply palette for custom color schemes
    if (paletteToApply) {
      this.renderer.setAttribute(this.document.documentElement, 'data-palette', paletteToApply.id);
      // Apply the palette colors
      this.updatePaletteColors(paletteToApply, isDarkMode);
    }
  }

  private updatePaletteColors(palette: ColorPalette, isDarkMode: boolean): void {
    const style = this.document.documentElement.style;

    // Define base colors
    const surfaceColor = isDarkMode ? '#1f1f1f' : '#f5f5f5';
    const onSurface = isDarkMode ? '#e0e0e0' : '#1f1f1f';

    // Generate color roles
    const primaryRoles = this.generateColorRoles(
      palette.primary,
      'primary',
      surfaceColor,
      isDarkMode,
    );
    const secondaryRoles = this.generateColorRoles(
      palette.secondary,
      'secondary',
      surfaceColor,
      isDarkMode,
    );
    const tertiaryRoles = this.generateColorRoles(
      palette.tertiary,
      'tertiary',
      surfaceColor,
      isDarkMode,
    );

    // Apply all color roles to CSS variables
    this.applyColorRoles(primaryRoles);
    this.applyColorRoles(secondaryRoles);
    this.applyColorRoles(tertiaryRoles);

    // Surface and outline colors
    style.setProperty('--mat-sys-surface', surfaceColor);
    style.setProperty('--mat-sys-surface-rgb', this.hexToRgb(surfaceColor));
    style.setProperty('--mat-sys-on-surface', onSurface);
    style.setProperty('--mat-sys-on-surface-rgb', this.hexToRgb(onSurface));

    const onSurfaceVariant = this.mixColor(onSurface, surfaceColor, isDarkMode ? 0.45 : 0.55);
    style.setProperty('--mat-sys-on-surface-variant', onSurfaceVariant);
    style.setProperty('--mat-sys-on-surface-variant-rgb', this.hexToRgb(onSurfaceVariant));

    // Surface container colors
    for (const level of ['lowest', 'low', 'container', 'high', 'highest']) {
      const mixRatio =
        {
          lowest: 0.05,
          low: 0.1,
          container: 0.15,
          high: 0.2,
          highest: 0.25,
        }[level] || 0;
      const containerColor = this.mixColor(palette.primary, surfaceColor, mixRatio);
      style.setProperty(`--mat-sys-surface-${level}`, containerColor);
    }

    // Outline colors
    const outlineColor = this.mixColor(onSurface, surfaceColor, isDarkMode ? 0.55 : 0.45);
    style.setProperty('--mat-sys-outline', outlineColor);
    style.setProperty('--mat-sys-outline-rgb', this.hexToRgb(outlineColor));

    const outlineVariantColor = this.mixColor(onSurface, surfaceColor, isDarkMode ? 0.65 : 0.35);
    style.setProperty('--mat-sys-outline-variant', outlineVariantColor);
    style.setProperty('--mat-sys-outline-variant-rgb', this.hexToRgb(outlineVariantColor));

    // Error and Success colors
    this.applyStatusColors('error', '#f44336', surfaceColor, isDarkMode);
    this.applyStatusColors('success', '#188038', surfaceColor, isDarkMode);

    // Inverse roles
    const inverseSurface = isDarkMode ? '#e6e6e6' : '#303030';
    const inverseOnSurface = isDarkMode ? '#303030' : '#f5f5f5';
    style.setProperty('--mat-sys-inverse-surface', inverseSurface);
    style.setProperty('--mat-sys-inverse-on-surface', inverseOnSurface);

    // Other properties
    style.setProperty('--mat-sys-shadow', 'rgba(0, 0, 0, 0.3)');
    style.setProperty('--mat-sys-scrim', 'rgba(0, 0, 0, 0.32)');
    style.setProperty('--mat-icon-color', 'currentColor');
  }

  private generateColorRoles(
    baseColor: string,
    name: 'primary' | 'secondary' | 'tertiary',
    surfaceColor: string,
    isDarkMode: boolean,
  ): Record<string, string> {
    const onColor = this.getReadableTextColor(baseColor);
    const container = isDarkMode
      ? this.mixColor(baseColor, surfaceColor, 0.1)
      : this.adjustColor(baseColor, 0.9);
    const onContainer = isDarkMode ? baseColor : this.adjustColor(baseColor, 0.1);

    return {
      [`--mat-sys-${name}`]: baseColor,
      [`--mat-sys-on-${name}`]: onColor,
      [`--mat-sys-${name}-container`]: container,
      [`--mat-sys-on-${name}-container`]: onContainer,
    };
  }

  private applyColorRoles(roles: Record<string, string>): void {
    const style = this.document.documentElement.style;
    for (const [key, value] of Object.entries(roles)) {
      style.setProperty(key, value);
      style.setProperty(`${key}-rgb`, this.hexToRgb(value));
    }
  }

  private applyStatusColors(
    name: 'error' | 'success',
    baseColor: string,
    surfaceColor: string,
    isDarkMode: boolean,
  ): void {
    const onColor = this.getReadableTextColor(baseColor);
    const container = isDarkMode
      ? this.mixColor(baseColor, surfaceColor, 0.1)
      : this.adjustColor(baseColor, 0.9);
    const onContainer = isDarkMode ? baseColor : this.adjustColor(baseColor, 0.1);

    const roles = {
      [`--mat-sys-${name}`]: baseColor,
      [`--mat-sys-on-${name}`]: onColor,
      [`--mat-sys-${name}-container`]: container,
      [`--mat-sys-on-${name}-container`]: onContainer,
    };

    this.applyColorRoles(roles);
  }

  private getEffectiveDarkMode(mode: ThemeMode): boolean {
    return mode === 'dark' || (mode === 'system' && !!this.darkThemeMediaQuery?.matches);
  }

  private hexToRgb(hex: string): string {
    const tuple = this.parseHexColor(hex);
    if (!tuple) {
      return '0, 0, 0';
    }
    const [r, g, b] = tuple;
    return `${r}, ${g}, ${b}`;
  }

  private getReadableTextColor(hex: string): string {
    const tuple = this.parseHexColor(hex);
    if (!tuple) {
      return '#ffffff';
    }

    const [r, g, b] = tuple.map((value) => value / 255) as [number, number, number];

    const transform = (channel: number): number =>
      channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);

    const luminance = 0.2126 * transform(r) + 0.7152 * transform(g) + 0.0722 * transform(b);

    return luminance > 0.55 ? '#000000' : '#ffffff';
  }

  private parseHexColor(hex: string): [number, number, number] | null {
    const normalized = hex.replace(/^#/, '');
    if (normalized.length !== 6) {
      return null;
    }

    const value = Number.parseInt(normalized, 16);
    if (Number.isNaN(value)) {
      return null;
    }

    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  }

  private adjustColor(hex: string, factor: number): string {
    // Remove the hash if present
    hex = hex.replace(/^#/, '');

    // Convert hex to RGB
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Adjust the color
    const adjustedR = Math.round(r + (255 - r) * factor);
    const adjustedG = Math.round(g + (255 - g) * factor);
    const adjustedB = Math.round(b + (255 - b) * factor);

    // Convert back to hex
    const newHex =
      '#' +
      adjustedR.toString(16).padStart(2, '0') +
      adjustedG.toString(16).padStart(2, '0') +
      adjustedB.toString(16).padStart(2, '0');

    return newHex;
  }

  private mixColor(color1: string, color2: string, ratio: number): string {
    const c1 = color1.replace(/^#/, '');
    const c2 = color2.replace(/^#/, '');

    const r1 = parseInt(c1.slice(0, 2), 16);
    const g1 = parseInt(c1.slice(2, 4), 16);
    const b1 = parseInt(c1.slice(4, 6), 16);

    const r2 = parseInt(c2.slice(0, 2), 16);
    const g2 = parseInt(c2.slice(2, 4), 16);
    const b2 = parseInt(c2.slice(4, 6), 16);

    const r = Math.round(r1 * ratio + r2 * (1 - ratio));
    const g = Math.round(g1 * ratio + g2 * (1 - ratio));
    const b = Math.round(b1 * ratio + b2 * (1 - ratio));

    const newHex =
      '#' +
      r.toString(16).padStart(2, '0') +
      g.toString(16).padStart(2, '0') +
      b.toString(16).padStart(2, '0');

    return newHex;
  }
}
